/** Gemeinsame Helfer der Nutzereinstellungen: Einstellungen speichern (optimistisch), eigenes Konto im Store setzen. */
import type { CurrentUserDto, MemberDto, UserSettingsDto } from '@lagerfeuer/shared';
import { errorMessage } from '../../lib/api';
import { usersApi } from '../../lib/endpoints';
import { appState } from '../../store/app';
import { toast } from '../../store/ui';
import { useThemePreview, type ThemePreview } from '../theme/ThemeManager';

export type SettingsPatch = Partial<Omit<UserSettingsDto, 'updatedAt'>>;

/** PATCH /users/@me/settings – sofort lokal anwenden, bei Fehler zurückrollen. */
export async function patchSettings(patch: SettingsPatch): Promise<UserSettingsDto | null> {
  const prev = appState().settings;
  appState().update((d) => {
    if (d.settings) Object.assign(d.settings, patch);
  });
  try {
    const res = await usersApi.updateSettings(patch);
    appState().update((d) => {
      d.settings = res;
    });
    return res;
  } catch (e) {
    appState().update((d) => {
      d.settings = prev;
    });
    toast.error('Einstellung konnte nicht gespeichert werden', errorMessage(e));
    return null;
  }
}

/** Antwort von /users/@me* übernehmen. */
export function applyMe(user: CurrentUserDto): void {
  appState().update((d) => {
    d.me = user;
    d.users[user.id] = { ...(d.users[user.id] ?? {}), ...user };
    for (const s of Object.values(d.servers)) if (s.me.user.id === user.id) s.me.user = { ...s.me.user, ...user };
  });
}

/** Antwort von /servers/:id/members/@me* übernehmen. */
export function applyMyMember(member: MemberDto): void {
  appState().update((d) => {
    const s = d.servers[member.serverId];
    if (s) s.me = member;
    (d.members[member.serverId] ??= {})[member.user.id] = member;
  });
}

/**
 * Ungespeicherte Darstellungs-Vorschau (ThemeManager) ändern, ohne die Vorschau anderer Editoren zu
 * überschreiben. `remove` entfernt Schlüssel wieder (gespeicherter Wert gilt dann erneut).
 */
export function mergePreview(patch: ThemePreview, remove: (keyof ThemePreview)[] = []): void {
  const cur: ThemePreview = { ...(useThemePreview.getState().preview ?? {}), ...patch };
  for (const k of remove) delete cur[k];
  useThemePreview.getState().setPreview(Object.keys(cur).length ? cur : null);
}

/** `#rrggbb` oder null. */
export function validHex(v: string | null | undefined): string | null {
  return v && /^#[0-9a-f]{6}$/i.test(v) ? v : null;
}
